import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PubMedComponent from '../components/PubMed/PubMedComponent';
import { normalizeReferences } from '../utils/referenceUtils';
import { LOADING_STATES } from '../utils/appUtils';
import { ditechUploadApi, ditechDocumentsApi } from '../constants/urls';
import { Icons } from '../components/common';

const CITATION_STYLES = [
  { value: 'vancouver', label: 'Vancouver (NLM/DiTech)' },
  { value: 'ama', label: 'AMA' },
  { value: 'apa', label: 'APA 7th' },
  { value: 'harvard', label: 'Harvard' },
  { value: 'chicago', label: 'Chicago (Author-Date)' },
];

const DiTechPage = () => {
  const navigate = useNavigate();

  // Upload / processing state
  const [file, setFile] = useState(null);
  const [citationStyle, setCitationStyle] = useState('vancouver');
  const [originalFilename, setOriginalFilename] = useState('');
  const [terms, setTerms] = useState({ isLoading: LOADING_STATES.IDLE, data: [] });
  const [uploadError, setUploadError] = useState('');

  // Save state
  const [documentName, setDocumentName] = useState('');
  const [processedRefs, setProcessedRefs] = useState([]);
  const [saveStatus, setSaveStatus] = useState('');
  const [saving, setSaving] = useState(false);

  // Saved documents list
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  const fetchDocuments = useCallback(async () => {
    setLoadingDocs(true);
    try {
      const res = await axios.get(ditechDocumentsApi, { withCredentials: true });
      if (res.data.success) setDocuments(res.data.documents);
    } catch (e) {
      console.error('Failed to fetch DiTech documents', e);
    } finally {
      setLoadingDocs(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setUploadError('');
    setSaveStatus('');
    setProcessedRefs([]);
    setTerms({ isLoading: LOADING_STATES.IDLE, data: [] });
  };

  const handleUpload = async () => {
    if (!file) { setUploadError('Please select a .docx file first.'); return; }
    setUploadError('');
    setSaveStatus('');
    setProcessedRefs([]);
    const formData = new FormData();
    formData.append('file', file);
    setTerms({ isLoading: LOADING_STATES.UPLOADING, data: [] });
    try {
      const res = await axios.post(ditechUploadApi, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      if (res.data.success && res.data.references.length > 0) {
        setOriginalFilename(res.data.originalFilename || file.name);
        if (!documentName) setDocumentName(file.name.replace(/\.docx$/i, ''));
        setTerms({
          isLoading: LOADING_STATES.PROCESSING,
          data: normalizeReferences(res.data.references),
        });
      } else {
        setUploadError('No references found in the document.');
        setTerms({ isLoading: LOADING_STATES.IDLE, data: [] });
      }
    } catch (e) {
      setUploadError('Upload failed. Please try again.');
      setTerms({ isLoading: LOADING_STATES.IDLE, data: [] });
    }
  };

  const handleSave = async () => {
    if (!documentName.trim()) { setSaveStatus('Please enter a document name.'); return; }
    if (processedRefs.length === 0) { setSaveStatus('No processed references to save yet.'); return; }
    setSaving(true);
    setSaveStatus('Saving…');
    try {
      const res = await axios.post(ditechDocumentsApi, {
        documentName: documentName.trim(),
        processedReferences: processedRefs,
        rawReferences: terms.data.map(r => r.term || r),
        originalFilename,
      }, { withCredentials: true });
      if (res.data.success) {
        setSaveStatus('Saved successfully.');
        fetchDocuments();
        // Reset for next document
        setFile(null);
        setOriginalFilename('');
        setTerms({ isLoading: LOADING_STATES.IDLE, data: [] });
        setProcessedRefs([]);
        setDocumentName('');
      }
    } catch (e) {
      setSaveStatus('Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const isProcessing = terms.isLoading === LOADING_STATES.PROCESSING || terms.isLoading === LOADING_STATES.UPLOADING;
  const hasRefs = terms.data.length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">DiTech Reference Processing</h1>
        <p className="text-sm text-gray-500 mt-1">Upload a DOCX, choose a citation style, process references, then save.</p>
      </div>

      {/* Upload + Save bar */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Citation style selector */}
          <div className="min-w-52">
            <label className="block text-sm font-medium text-gray-700 mb-1">Citation Style</label>
            <select
              value={citationStyle}
              onChange={e => {
                setCitationStyle(e.target.value);
                setProcessedRefs([]);
                setTerms({ isLoading: LOADING_STATES.IDLE, data: [] });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              {CITATION_STYLES.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          {/* File picker */}
          <div className="flex-1 min-w-56">
            <label className="block text-sm font-medium text-gray-700 mb-1">DOCX File</label>
            <div className="flex items-center gap-2">
              <label className="cursor-pointer inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-colors">
                <Icons.UploadIcon className="w-4 h-4 mr-2" />
                {file ? file.name : 'Choose File'}
                <input type="file" accept=".docx" onChange={handleFileChange} className="hidden" />
              </label>
              <button
                onClick={handleUpload}
                disabled={!file || isProcessing}
                className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Extract & Process
              </button>
            </div>
            {uploadError && <p className="text-red-600 text-xs mt-1">{uploadError}</p>}
          </div>

          {/* Document name + save (only when refs ready) */}
          {hasRefs && (
            <div className="flex-1 min-w-72">
              <label className="block text-sm font-medium text-gray-700 mb-1">Document Name</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={documentName}
                  onChange={e => setDocumentName(e.target.value)}
                  placeholder="e.g. DiTech-Chapter-01"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={handleSave}
                  disabled={saving || processedRefs.length === 0}
                  className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                >
                  {saving ? 'Saving…' : 'Save Document'}
                </button>
              </div>
              {saveStatus && (
                <p className={`text-xs mt-1 ${saveStatus.includes('success') ? 'text-green-600' : saveStatus.includes('Saving') ? 'text-blue-600' : 'text-red-600'}`}>
                  {saveStatus}
                </p>
              )}
              {processedRefs.length > 0 && !saving && !saveStatus && (
                <p className="text-xs text-gray-500 mt-1">{processedRefs.length} references ready to save</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* PubMed component */}
      {hasRefs && (
        <PubMedComponent
          terms={terms}
          dbMode={false}
          mode="ditech"
          citationStyle={citationStyle}
          onProcessed={setProcessedRefs}
        />
      )}

      {/* Saved documents list */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Saved DiTech Documents</h2>
          <button onClick={fetchDocuments} className="text-sm text-blue-600 hover:text-blue-800">
            Refresh
          </button>
        </div>

        {loadingDocs ? (
          <div className="p-6 text-center text-gray-500 text-sm">Loading…</div>
        ) : documents.length === 0 ? (
          <div className="p-6 text-center text-gray-500 text-sm">No documents saved yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left font-medium text-gray-600">Document Name</th>
                <th className="px-6 py-3 text-left font-medium text-gray-600">Original File</th>
                <th className="px-6 py-3 text-center font-medium text-gray-600">References</th>
                <th className="px-6 py-3 text-left font-medium text-gray-600">Saved By</th>
                <th className="px-6 py-3 text-left font-medium text-gray-600">Date</th>
                <th className="px-6 py-3 text-center font-medium text-gray-600">View</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {documents.map(doc => (
                <tr key={doc.id} className="hover:bg-gray-50">
                  <td className="px-6 py-3 font-medium text-gray-900">{doc.documentName}</td>
                  <td className="px-6 py-3 text-gray-500">{doc.originalFilename || '—'}</td>
                  <td className="px-6 py-3 text-center text-gray-700">{doc.totalReferences}</td>
                  <td className="px-6 py-3 text-gray-500">{doc.uploadedBy}</td>
                  <td className="px-6 py-3 text-gray-500">
                    {doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-6 py-3 text-center">
                    <button
                      onClick={() => navigate(`/ditech/documents/${doc.id}`)}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default DiTechPage;
